import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import allocabLogo from '../assets/images/allocab-logo.svg';
import { Link } from '../components/atomic/Link';
import { TextInput } from '../components/atomic/TextInput';
import { PrimaryButton } from '../components/button/PrimaryButton';
import { useAuth } from '../hooks/useAuth';
import { t } from '../services/translation/translation';

type FieldValues = {
  email: string;
  password: string;
};

export const LoginRoute = () => {
  const { signinMutation } = useAuth();
  const { state } = useLocation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FieldValues>({ defaultValues: { email: state?.initialEmail } });
  const email = watch('email');

  const onSubmit = handleSubmit(({ email, password }) => {
    signinMutation.mutate({ email, password });
  });

  return (
    <section className="bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto min-h-screen">
        <div className="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
          <img className="h-10 w-auto" src={allocabLogo} alt="allocab" />
        </div>
        <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
              {t('login-route.title')}
            </h1>
            <form className="space-y-4 md:space-y-6" onSubmit={onSubmit}>
              <TextInput
                type="email"
                id="email"
                placeholder="jean@dupont.com"
                label={t('common.email')}
                errorMessage={errors.email?.message}
                {...register('email', { required: true })}
              />

              <TextInput
                type="password"
                id="password"
                placeholder="••••••••"
                label={t('common.password')}
                errorMessage={errors.password?.message}
                {...register('password', { required: true })}
              />

              <div className="gap-2 flex flex-col items-end">
                <Link
                  to="/reset-password"
                  state={{ initialEmail: email }}
                  className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-500"
                >
                  {t('login-route.forgot-password')}
                </Link>

                <PrimaryButton
                  text={t('login-route.cta')}
                  type="submit"
                  isLoading={signinMutation.isLoading}
                  className="w-full"
                />
              </div>
            </form>
            <p className="text-sm font-light text-gray-500 dark:text-gray-400">
              {t('login-route.no-account-yet')}
              <Link
                to="/sign-up"
                state={{ initialEmail: email }}
                className="font-medium text-blue-600 hover:underline dark:text-blue-500 ml-1"
              >
                {t('login-route.sign-up')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
